import { useState, useMemo } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import { Badge } from '@/components/ui/badge';
import {
  BriefcaseMedical, Search, Clock, ChevronDown, X, User,
  Heart, FileText, Download, Calendar, CheckCircle2, Building2,
  AlertTriangle, ArrowRight,
} from 'lucide-react';

/* ═══ MOCK ═══ */
const DIMISSIONI = [
  { id: 1, paziente: 'Bianchi Mario', eta: 74, cf: 'BNCMRA52B12H501G', struttura: 'Policlinico Gemelli — Neurologia', dataDimissione: 'Oggi', complessita: 'Alta', stato: 'in-attesa', diagnosi: 'Ictus ischemico emisfero sinistro con emiparesi destra residua', bisogni: 'Riabilitazione neuromotoria intensiva, Logopedia, Monitoraggio PA e FC, Supporto psicologico caregiver', allegato: 'Lettera_Dimissione_Gemelli_Bianchi.pdf' },
  { id: 2, paziente: 'Mancini Rosa', eta: 91, cf: 'MNCRSA35T51H501I', struttura: 'Ospedale S. Camillo — Oncologia', dataDimissione: 'Oggi', complessita: 'Alta', stato: 'in-attesa', diagnosi: 'Neoplasia polmonare stadio IV — cure palliative', bisogni: 'Terapia del dolore (pompa elastomerica), Nutrizione assistita, Ossigenoterapia H24, Supporto spirituale', allegato: 'Lettera_Dimissione_SCamillo_Mancini.pdf' },
  { id: 3, paziente: 'Conti Alberto', eta: 79, cf: 'CNTALB47H15H501L', struttura: 'Ospedale S. Andrea — Ortopedia', dataDimissione: '17/04/2026', complessita: 'Media', stato: 'in-attesa', diagnosi: 'Frattura pertrocanterica femore dx — protesi', bisogni: 'Fisioterapia domiciliare 5/sett, Medicazione ferita chirurgica, Profilassi TEV', allegato: 'Lettera_Dimissione_SAndrea_Conti.pdf' },
  { id: 4, paziente: 'Lombardi Franca', eta: 84, cf: 'LMBFRC42R60H501M', struttura: 'Policlinico Tor Vergata — Geriatria', dataDimissione: '18/04/2026', complessita: 'Media', stato: 'in-lavorazione', diagnosi: 'Scompenso cardiaco cronico riacutizzato', bisogni: 'Monitoraggio parametri (PA, FC, peso, SpO2), Gestione terapia diuretica, Educazione sanitaria caregiver', allegato: 'Lettera_Dimissione_TorVergata_Lombardi.pdf' },
  { id: 5, paziente: 'Greco Salvatore', eta: 70, cf: 'GRCSLV56D18H501N', struttura: 'Ospedale Pertini — Chirurgia', dataDimissione: '20/04/2026', complessita: 'Bassa', stato: 'in-attesa', diagnosi: 'Colecistectomia laparoscopica — post-operatorio', bisogni: 'Medicazione ferita, Monitoraggio parametri post-chirurgici', allegato: 'Lettera_Dimissione_Pertini_Greco.pdf' },
  { id: 6, paziente: 'Vitale Teresa', eta: 77, cf: 'VTLTRS49A45H501P', struttura: 'Ospedale S. Camillo — Medicina Interna', dataDimissione: '15/04/2026', complessita: 'Alta', stato: 'completata', diagnosi: 'Polmonite bilaterale comunitaria su BPCO', bisogni: 'Ossigenoterapia, Aerosolterapia, Fisioterapia respiratoria, Monitoraggio SpO2', allegato: 'Lettera_Dimissione_SCamillo_Vitale.pdf' },
];

const EROGATORI = ['Coop. Salute Roma', 'Consorzio ADI Lazio', 'Fondazione Gemelli@Home', 'ASL Roma 2 — Servizio Territoriale', 'Coop. Riabilita Roma'];
const CASE_MANAGERS = ['Inf. L. Conti', 'Inf. M. Ferrara', 'Inf. A. Romano', 'Inf. G. Neri', 'Inf. S. Marchetti'];
const STRUTTURE = [...new Set(DIMISSIONI.map(d => d.struttura.split('—')[0].trim()))];

const COMP_STYLE = {
  Alta: { bg: 'bg-red-50 dark:bg-red-950/30', text: 'text-red-700 dark:text-red-400' },
  Media: { bg: 'bg-amber-50 dark:bg-amber-950/30', text: 'text-amber-700 dark:text-amber-400' },
  Bassa: { bg: 'bg-emerald-50 dark:bg-emerald-950/30', text: 'text-emerald-700 dark:text-emerald-400' },
};

const STATO_STYLE = {
  'in-attesa': { bg: 'bg-orange-50 dark:bg-orange-950/30', text: 'text-orange-700 dark:text-orange-400', dot: 'bg-orange-500', label: 'In Attesa' },
  'in-lavorazione': { bg: 'bg-blue-50 dark:bg-blue-950/30', text: 'text-blue-700 dark:text-blue-400', dot: 'bg-blue-500', label: 'In Lavorazione' },
  'completata': { bg: 'bg-emerald-50 dark:bg-emerald-950/30', text: 'text-emerald-700 dark:text-emerald-400', dot: 'bg-emerald-500', label: 'Completata' },
};

export default function DimissioniProtette() {
  const [search, setSearch] = useState('');
  const [filterStato, setFilterStato] = useState('tutti');
  const [filterStruttura, setFilterStruttura] = useState('tutte');
  const [selectedDischarge, setSelectedDischarge] = useState(null);
  const [erogatore, setErogatore] = useState('');
  const [cm, setCm] = useState('');
  const [dataPrimoAccesso, setDataPrimoAccesso] = useState('');

  const countInAttesa = DIMISSIONI.filter(d => d.stato === 'in-attesa').length;
  const countOggi = DIMISSIONI.filter(d => d.dataDimissione === 'Oggi').length;
  const countCompletate = DIMISSIONI.filter(d => d.stato === 'completata').length;

  const filtered = useMemo(() => {
    return DIMISSIONI.filter(d => {
      if (search) {
        const q = search.toLowerCase();
        if (!d.paziente.toLowerCase().includes(q) && !d.struttura.toLowerCase().includes(q)) return false;
      }
      if (filterStato !== 'tutti' && d.stato !== filterStato) return false;
      if (filterStruttura !== 'tutte' && !d.struttura.startsWith(filterStruttura)) return false;
      return true;
    });
  }, [search, filterStato, filterStruttura]);

  const openIntake = (d) => {
    setSelectedDischarge(d);
    setErogatore('');
    setCm('');
    setDataPrimoAccesso('');
  };

  return (
    <AppLayout role="segreteria">
      <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-6" data-testid="dimissioni-protette-page">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Dimissioni Protette</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Gestione transizioni Ospedale-Territorio e presa in carico ADI</p>
        </div>

        {/* KPI */}
        <div className="grid grid-cols-3 gap-4" data-testid="dimissioni-kpi">
          <div className="bg-orange-50 dark:bg-orange-950/20 rounded-2xl border border-orange-200 dark:border-orange-800/40 p-5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-orange-500 uppercase tracking-wider">In Attesa di Presa in Carico</span>
              <Clock className="w-5 h-5 text-orange-400" />
            </div>
            <p className="text-3xl font-bold text-orange-700 dark:text-orange-400">{countInAttesa}</p>
            <p className="text-[10px] text-orange-500 dark:text-orange-400/70 mt-0.5">Richieste da strutture ospedaliere</p>
          </div>
          <div className="bg-red-50 dark:bg-red-950/20 rounded-2xl border border-red-200 dark:border-red-800/40 p-5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-red-500 uppercase tracking-wider">Dimissioni Previste Oggi</span>
              <AlertTriangle className="w-5 h-5 text-red-400" />
            </div>
            <p className="text-3xl font-bold text-red-700 dark:text-red-400">{countOggi}</p>
            <p className="text-[10px] text-red-500 dark:text-red-400/70 mt-0.5">Urgenza logistica — attivazione immediata</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Attivazioni Completate (Mese)</span>
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            </div>
            <p className="text-3xl font-bold text-slate-800 dark:text-white">45</p>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">Transizioni H→T concluse con successo</p>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4 flex flex-wrap items-center gap-3" data-testid="dimissioni-toolbar">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca paziente o struttura..." className="w-full h-10 pl-10 pr-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20" data-testid="search-dimissioni" />
          </div>
          <div className="relative">
            <select value={filterStato} onChange={e => setFilterStato(e.target.value)} className="h-10 px-3 pr-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-medium text-slate-700 dark:text-slate-200 appearance-none focus:outline-none focus:ring-2 focus:ring-orange-500/20" data-testid="filter-stato">
              <option value="tutti">Tutti gli stati</option>
              <option value="in-attesa">In Attesa</option>
              <option value="in-lavorazione">In Lavorazione</option>
              <option value="completata">Completate</option>
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
          </div>
          <div className="relative">
            <select value={filterStruttura} onChange={e => setFilterStruttura(e.target.value)} className="h-10 px-3 pr-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-medium text-slate-700 dark:text-slate-200 appearance-none focus:outline-none focus:ring-2 focus:ring-orange-500/20" data-testid="filter-struttura">
              <option value="tutte">Tutte le strutture</option>
              {STRUTTURE.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden" data-testid="dimissioni-table">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700/60">
                {['Paziente', 'Struttura Inviante', 'Dimissione Prevista', 'Complessità', 'Stato', ''].map(col => (
                  <th key={col} className="px-5 py-3 text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-slate-400">Nessuna dimissione trovata.</td></tr>
              ) : filtered.map(d => {
                const cs = COMP_STYLE[d.complessita];
                const ss = STATO_STYLE[d.stato];
                const isToday = d.dataDimissione === 'Oggi';
                return (
                  <tr key={d.id} className={`border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors ${isToday ? 'bg-red-50/30 dark:bg-red-950/10' : ''}`} data-testid={`dimissione-row-${d.id}`}>
                    <td className="px-5 py-3.5">
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{d.paziente}</p>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{d.eta} anni · {d.cf.substring(0,6)}...</p>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span className="text-xs text-slate-600 dark:text-slate-300">{d.struttura}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      {isToday ? (
                        <Badge className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-0 text-[10px] font-bold animate-pulse">OGGI</Badge>
                      ) : (
                        <span className="text-xs text-slate-600 dark:text-slate-300">{d.dataDimissione}</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <Badge className={`${cs.bg} ${cs.text} border-0 text-[10px] font-semibold`}>{d.complessita}</Badge>
                    </td>
                    <td className="px-5 py-3.5">
                      <Badge className={`${ss.bg} ${ss.text} border-0 text-[10px] font-semibold gap-1`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${ss.dot}`} />
                        {ss.label}
                      </Badge>
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      {d.stato !== 'completata' ? (
                        <button onClick={() => openIntake(d)} className="flex items-center gap-1.5 text-xs font-semibold text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 transition-colors" data-testid={`gestisci-intake-${d.id}`}>
                          Gestisci Intake <ArrowRight className="w-3 h-3" />
                        </button>
                      ) : (
                        <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1 justify-end"><CheckCircle2 className="w-3 h-3" /> Completata</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ══ DRAWER: GESTIONE INTAKE ══ */}
      {selectedDischarge && (
        <div className="fixed inset-0 z-50" data-testid="drawer-intake">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSelectedDischarge(null)} />
          <div className="absolute top-0 right-0 h-full w-full max-w-[600px] bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-800 flex flex-col">

            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                    <BriefcaseMedical className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-slate-800 dark:text-white">Presa in Carico: {selectedDischarge.paziente}</h2>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">{selectedDischarge.struttura}</p>
                  </div>
                </div>
                <button onClick={() => setSelectedDischarge(null)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-testid="drawer-close">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-6">

              {/* Sez 1: Dati Clinici Dimissione */}
              <div>
                <h3 className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">Dati Clinici di Dimissione</h3>
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border border-slate-200 dark:border-slate-700 flex flex-col gap-3">
                  <div className="flex items-center gap-2 text-xs">
                    <User className="w-3.5 h-3.5 text-slate-400" />
                    <span className="font-bold text-slate-800 dark:text-white">{selectedDischarge.paziente}</span>
                    <span className="text-slate-500">{selectedDischarge.eta} anni</span>
                    <span className="text-slate-400 font-mono text-[10px]">{selectedDischarge.cf}</span>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Diagnosi di Dimissione</p>
                    <p className="text-sm text-slate-800 dark:text-white font-medium">{selectedDischarge.diagnosi}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Bisogni Assistenziali Identificati</p>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {selectedDischarge.bisogni.split(', ').map((b, i) => (
                        <Badge key={i} className="bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-0 text-[10px] font-medium">{b}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex-1">
                      <FileText className="w-4 h-4 text-red-500 shrink-0" />
                      <span className="text-xs text-slate-700 dark:text-slate-200 truncate">{selectedDischarge.allegato}</span>
                    </div>
                    <button className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-testid="download-lettera">
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center gap-3 text-[11px]">
                    <span className="text-slate-400">Dimissione prevista:</span>
                    {selectedDischarge.dataDimissione === 'Oggi' ? (
                      <Badge className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-0 text-[10px] font-bold">OGGI — Urgente</Badge>
                    ) : (
                      <span className="font-medium text-slate-700 dark:text-slate-200">{selectedDischarge.dataDimissione}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Sez 2: Assegnazione Rete Territoriale */}
              <div>
                <h3 className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">Assegnazione Rete Territoriale</h3>
                <div className="flex flex-col gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Erogatore / Provider ADI *</label>
                    <div className="relative">
                      <select value={erogatore} onChange={e => setErogatore(e.target.value)} className="w-full h-10 px-3 pr-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 appearance-none focus:outline-none focus:ring-2 focus:ring-orange-500/30" data-testid="select-erogatore">
                        <option value="">Seleziona erogatore...</option>
                        {EROGATORI.map(e => <option key={e} value={e}>{e}</option>)}
                      </select>
                      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Assegna Case Manager *</label>
                    <div className="relative">
                      <select value={cm} onChange={e => setCm(e.target.value)} className="w-full h-10 px-3 pr-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 appearance-none focus:outline-none focus:ring-2 focus:ring-orange-500/30" data-testid="select-cm">
                        <option value="">Seleziona case manager...</option>
                        {CASE_MANAGERS.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Data Programmata Primo Accesso Domiciliare</label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input type="date" value={dataPrimoAccesso} onChange={e => setDataPrimoAccesso(e.target.value)} className="w-full h-10 pl-10 pr-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500/30" data-testid="input-data-accesso" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 shrink-0 flex items-center justify-between gap-3">
              <button className="px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 text-xs font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" data-testid="btn-richiedi-integrazione">
                Richiedi Integrazione Ospedale
              </button>
              <button
                disabled={!erogatore || !cm}
                className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2
                  ${erogatore && cm
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/20'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 cursor-not-allowed'}`}
                data-testid="btn-attiva-adi"
              >
                <CheckCircle2 className="w-4 h-4" /> Attiva ADI e Notifica Équipe
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
